<template lang="pug">
  .cart
    transition(name="fade", mode="out-in")
      .cart__wrapper
        .cart__contents
          .cart__header
            .cart__count {{ cartCountText }}
            h1.cart__heading {{ heading }}

          transition-group.cart__items(
            name="slide-fade-left"
            tag="ul"
            v-if="totalItems > 0"
            key="contents"
          )
            cart-line-item(
              v-for="(item, index) in items",
              :item="item",
              :line="index + 1",
              :key="item.id",
              @gwp-cancelled="cancelThresholdProduct"
            )

          .cart__empty(v-else, key="empty")
            p.cart__empty-text {{ emptyText }}
            base-button.cart__empty-cta(
              v-if="emptyCartCtaUrl && emptyCartCtaText"
              :href="emptyCartCtaUrl"
            ) {{ emptyCartCtaText }}
            base-button.cart__empty-cta.cart__empty-cta--secondary.button--green(
              v-if="emptyCartSecondaryCtaUrl && emptyCartSecondaryCtaText"
              :href="emptyCartSecondaryCtaUrl"
            ) {{ emptyCartSecondaryCtaText }}

        .cart__checkout-column
          .cart__totals
            cart-totals-table
            .cart__promo-message(
              v-if="promoMessage"
              v-html="promoMessage"
            )
          .cart__form
            base-button.button--full-width.cart__checkout-cta(
              :href="checkoutURL"
              name="checkout"
              type="submit"
            ) Checkout
          base-button.button--secondary.cart__continue-shopping(
            href="/collections/all-products"
          ) Continue Shopping

</template>

<script>
  import { mapState } from 'vuex';
  import { gwpTypes, gwpStorageCancelledKeys } from 'scripts/config/gwp';
  import { getRechargeURL } from 'scripts/helpers/recharge.js';

  import BaseButton from 'scripts/components/buttons/BaseButton.vue';
  import CartLineItem from 'scripts/components/cart/CartLineItem.vue';
  import CartTotalsTable from 'scripts/components/cart/CartTotalsTable.vue';

  const GWP_TYPE_THRESHOLD = gwpTypes.threshold;
  const GWP_THRESHOLD_CANCELLED_KEY = gwpStorageCancelledKeys.threshold;

  export default {
    name: 'Cart',
    components: {
      BaseButton,
      CartLineItem,
      CartTotalsTable,
    },
    props: {
      heading: String,
      promoMessage: String,
      emptyText: String,
      emptyCartCtaText: String,
      emptyCartCtaUrl: String,
      emptyCartSecondaryCtaText: String,
      emptyCartSecondaryCtaUrl: String,
      productColumnLabel: String,
      priceColumnLabel: String,
      quantityColumnLabel: String,
      totalColumnLabel: String,
      emptyImage: String,
      emptyImageAlt: String,
      thresholdProductHandle: String,
      customerId: String,
      customerEmail: String,
      permanentDomain: String,
    },
    computed: {
      ...mapState({
        allProducts: (state) => state.products.all,
        items: (state) => state.cart.items,
        itemsSubtotal: (state) => state.cart.items_subtotal_price,
        totalDiscount: (state) => state.cart.total_discount,
        totalPrice: (state) => state.cart.total_price,
        totalItems: (state) => state.cart.item_count,
      }),
      cartCountText() {
        return this.items.length === 1 ? '1 item' : `${this.items.length} items`;
      },
      hasSubscriptionProducts() {
        return !!this.items.find((product) => product.properties && product.properties.subscription_id);
      },
      checkoutURL() {
        return this.hasSubscriptionProducts ? getRechargeURL(this.permanentDomain, this.customerId, this.customerEmail) : '/checkout';
      },
      thresholdProduct() {
        return this.thresholdProductHandle ? this.allProducts.find((p) => p.handle === this.thresholdProductHandle) : false;
      },
      hasThresholdProduct() {
        const thresholdProductItem = this.items
          ? this.items.find((p) => {
              return this.thresholdProduct && p.id === this.thresholdProduct.variants[0].id && p.properties && p.properties._gwp === GWP_TYPE_THRESHOLD;
            })
          : false;

        return this.thresholdProduct ? Boolean(thresholdProductItem) : false;
      },
    },
    methods: {
      cancelThresholdProduct() {
        window.sessionStorage.setItem(GWP_THRESHOLD_CANCELLED_KEY, 'true');
      },
    },
  };
</script>

<style lang="scss">
  .cart {
    position: relative;
    padding: ($spacing-unit * 6) ($spacing-unit * 4) ($spacing-unit * 20);
  }

  .cart__wrapper {
    @include tablet-up {
      display: flex;
      justify-content: space-between;
      margin: 0 auto;
      max-width: 930px;
    }
  }

  .cart__contents {
    margin-bottom: $spacing-unit * 5;

    @include tablet-up {
      margin-bottom: 0;
      padding-right: $spacing-unit * 4;
      width: 50%;
    }
  }

  .cart__header {
    position: relative;
    padding-bottom: $spacing-unit * 8;
    height: ($spacing-unit * 17);
    line-height: $spacing-unit * 10;
    border-bottom: 1px solid $color-green-900;
  }

  .cart__count {
    display: inline;
  }

  .cart__heading {
    position: absolute;
    top: 0;
    margin: 0;
    width: 100%;
    font-size: rem(40);
    line-height: rem(40);
    text-align: center;
  }

  .cart__items {
    margin: 0;
    padding: 0;
  }

  .cart__checkout-column {
    text-align: center;

    @include tablet-up {
      padding-left: $spacing-unit * 4;
      width: 50%;
    }
  }

  .cart__totals {
    margin-bottom: $spacing-unit * 4;
    padding: ($spacing-unit * 5) ($spacing-unit * 4);
    font-size: rem(18);
    line-height: rem(22);
    background: $color-green-900;

    @include tablet-up {
      margin-bottom: $spacing-unit * 4;
      font-size: rem(20);
    }

    table {
      padding-bottom: $spacing-unit * 4;
      width: 100%;
      border-bottom: 1px solid $color-green-900;
    }

    th {
      font-weight: $font-weight-medium;
      text-align: left;
    }

    td {
      text-align: right;
    }
  }

  .cart__promo-message {
    margin-top: $spacing-unit * 4;
    font-size: rem(14);
    line-height: rem(20);
    text-align: center;
  }

  .cart__empty {
    display: flex;
    padding: ($spacing-unit * 10) 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .cart__empty-text {
    margin-bottom: $spacing-unit * 10;
    color: $color-green-900;
    font-size: rem(18);
    line-height: rem(20);
    letter-spacing: rem(1.13);
    text-transform: uppercase;
  }

  .cart__empty-cta {
    margin-bottom: $spacing-unit * 4;
    padding: ($spacing-unit * 3) ($spacing-unit * 5);
    width: 250px;
    white-space: normal;
  }

  .cart__empty-cta--secondary {}

  .cart__form {
    margin-bottom: $spacing-unit * 5;
  }

  .cart__continue-shopping {
    color: $color-green-900;
  }
</style>
